import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, MailWarning } from "lucide-react";
import { errorMessage, fieldErrors } from "../api/client";
import { publicApi } from "../api/resources";
import type { ConsentText, RegistrationResult } from "../api/types";
import { PhoneVerifyPanel } from "../components/PhoneVerifyPanel";
import { AuthLayout } from "../components/AuthLayout";
import { Button, ErrorText, Field, FullScreenLoader, TextInput } from "../components/ui";

const INVALID = "This invitation link is invalid or has expired. Please contact your administrator.";

function Notice({ testId, icon, text }: { testId: string; icon: "ok" | "warn"; text: string }) {
  const Icon = icon === "ok" ? CheckCircle2 : MailWarning;
  return (
    <div data-testid={testId} className="flex items-start gap-3 rounded-sm border border-white/15 bg-surface p-6 text-sm text-zinc-200">
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${icon === "ok" ? "text-emerald-400" : "text-warn"}`} />
      <p>{text}</p>
    </div>
  );
}

export default function Register() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"checking" | "invalid" | "form" | "verify" | "done">(token ? "checking" : "invalid");
  const [success, setSuccess] = useState("");
  const [reg, setReg] = useState<RegistrationResult | null>(null);

  useEffect(() => {
    if (!token) return;
    publicApi.validateToken(token).then((r) => setState(r.valid ? "form" : "invalid")).catch(() => setState("invalid"));
  }, [token]);

  return (
    <AuthLayout testId="register-page" eyebrow="Crew registration" title="Join the crew"
      subtitle="Confirm your details to finish your GamedayTime invitation.">
      {!token && <Notice testId="register-no-token" icon="warn" text="Please use the registration link from your invitation email." />}
      {token && state === "checking" && <FullScreenLoader />}
      {token && state === "invalid" && <Notice testId="register-invalid-token" icon="warn" text={INVALID} />}
      {state === "done" && <Notice testId="register-success" icon="ok" text={success} />}
      {state === "form" && (
        <RegisterForm token={token} onInvalid={() => setState("invalid")}
          onDone={(r) => { setReg(r); setSuccess(r.message); setState(r.verification_token ? "verify" : "done"); }} />
      )}
      {state === "verify" && reg?.verification_token && (
        <div data-testid="register-verify-step" className="space-y-6">
          <p className="label-caps text-signal">Step 2 of 2 · Verify your phone</p>
          <PhoneVerifyPanel phoneMasked={reg.phone_masked ?? "your phone"} sent={reg.code_sent} initialError={reg.verification_error}
            onResend={() => publicApi.verifyResend(reg.verification_token!)} onCheck={(c) => publicApi.verifyCheck(reg.verification_token!, c)}
            onVerified={(m) => { setSuccess(m); setState("done"); }} />
          <p className="text-xs text-zinc-500">Didn't get it? You can also verify later from My Profile after signing in with your email.</p>
        </div>
      )}
      {state === "done" && reg?.verification_token && (
        <Link to="/login" data-testid="register-signin-link" className="mt-6 inline-block text-sm text-signal hover:text-white">Sign in →</Link>
      )}
    </AuthLayout>
  );
}

function RegisterForm({ token, onDone, onInvalid }: { token: string; onDone: (r: RegistrationResult) => void; onInvalid: () => void }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [consent, setConsent] = useState(false);
  const [consentText, setConsentText] = useState<ConsentText | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  useEffect(() => {
    publicApi.consentText().then(setConsentText).catch(() => setConsentText(null));
  }, []);

  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const res = await publicApi.register({ token, ...form, consent_acknowledged: consent });
      onDone(res);
    } catch (err) {
      const f = fieldErrors(err);
      if (Object.keys(f).length) setFields(f);
      else if (errorMessage(err) === INVALID) onInvalid();
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="register-form" noValidate>
      {(["full_name", "email", "phone"] as const).map((k) => (
        <Field key={k} label={{ full_name: "Full name", email: "Email (the one your invite was sent to)", phone: "Mobile phone" }[k]} htmlFor={`reg-${k}`}>
          <TextInput id={`reg-${k}`} value={form[k]} onChange={set(k)} required
            type={k === "email" ? "email" : k === "phone" ? "tel" : "text"}
            autoComplete={{ full_name: "name", email: "email", phone: "tel" }[k]}
            placeholder={k === "phone" ? "(555) 123-4567" : undefined}
            data-testid={`register-${k.replace("_", "-")}-input`} />
          {fields[k] && <p data-testid={`register-${k.replace("_", "-")}-error`} className="text-xs text-red-300">{fields[k]}</p>}
        </Field>
      ))}
      <div className={`rounded-sm border p-4 ${consentText?.finalized ? "border-white/15 bg-white/[0.04]" : "border-warn/40 bg-warn/5"}`}>
        {consentText && !consentText.finalized
          ? <p data-testid="register-consent-draft-label" className="label-caps text-warn">{consentText.label || "DRAFT – pending client approval"} · {consentText.version}</p>
          : <p data-testid="register-consent-version" className="label-caps text-zinc-400">SMS consent · {consentText?.version}</p>}
        <label className="mt-3 flex cursor-pointer items-start gap-3 text-sm text-zinc-300">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[#ff1d00]" data-testid="register-consent-checkbox" />
          <span data-testid="register-consent-text">{consentText?.text ?? "Loading consent text…"}</span>
        </label>
        <p className="mt-2 pl-7 text-xs text-zinc-400">
          <a href="/privacy" target="_blank" rel="noreferrer" className="text-signal underline" data-testid="register-privacy-link">Privacy Policy</a>
          {" · "}
          <a href="/terms" target="_blank" rel="noreferrer" className="text-signal underline" data-testid="register-terms-link">Terms</a>
        </p>
        {fields.consent_acknowledged && <p data-testid="register-consent-error" className="mt-2 text-xs text-red-300">You must acknowledge to continue.</p>}
      </div>
      <ErrorText message={error} testId="register-error" />
      <Button type="submit" loading={busy} disabled={!consent} className="w-full" data-testid="register-submit-button">
        Submit registration
      </Button>
    </form>
  );
}
